/**
 * What the wide rail lists, and in what order — ADR 9023 (bata-ui#196 §1, §4, §7).
 *
 * The rail is grouped BY APP, each group a dropdown: Pricing, Replenishment, Compacting,
 * Management, then "Future expansions". This supersedes ADR 9004's amendment of 2026-09-27
 * (a flat list, no collapsible chapters). What did not change: a path is named here and
 * nowhere else, the route behind it is the manifest's, and the URL does not move — the MENU
 * entry moves, never the route.
 *
 * `RAIL_APPS` (in `src/features/rail-apps.ts` since ADR 9035, re-exported here) is the whole of that decision, by path, in the order the rail draws it. It is a
 * product call the owner makes, so it is one list in one place rather than a flag spread over
 * seven manifests. A path here that no manifest offers, or that its manifest marks `disabled`,
 * is dropped rather than drawn, and `rail-layout.test.ts` fails on it so the list cannot rot
 * silently. Every navigation entry of every manifest that no app lists is drawn under "Future
 * expansions", inert.
 *
 * An entry may carry a `query`: Simulate is ONE screen reached from two apps with its stage
 * already set (`?stage=price`, `?stage=distribute`), and Documentation is one screen opened on
 * the app's own section (`?section=`). Nothing is duplicated behind them.
 *
 * An entry may also carry SUB-ENTRIES — ADR 9023's amendment of 2026-09-30 (DEC-84, client
 * rule 1: "the sub-pages linked from inside pages should be sub-menu entries on the left, as
 * before"). They are not typed here: `subEntries: 'manifest'` draws the manifest entry's own
 * declared `children`, in their `order`, so a view is named in its manifest and nowhere else.
 * Which entry shows them is still this list's call: a manifest that adds children does not
 * change the rail by itself. One sub-entry is the rail's own (`docs/adr/9064`): `parameters` on an
 * entry draws a last «Parametri», the register opened on the app and on that entry's key family
 * (`?app=&keys=`), with a query of its own rather than its parent's.
 *
 * THE COMMON ENTRIES — DEC-89, brief part 2 §1 (2026-09-30). Every app ends with the same three,
 * in the same order, after a `#3a2b2b` rule (`rail-hairline`): Parametri · Approvazioni in attesa
 * · Documentazione. `commonEntries` builds them, so the order cannot drift between apps:
 *
 * - **Parametri** is the register opened on the app's own keys (`?app=`, ADR 9025) — the page's
 *   scope, set here and read by the page; the page has no tabs. Management's is «Parametri
 *   generali» and draws the register's sub-entries (Registro · Scadenze e storico).
 * - **Approvazioni in attesa** is the app's own decision queue where it has one (Pricing's price
 *   steps, Replenishment's allocation lines, Compacting's transfers), and otherwise the
 *   parameters' queue opened on the app (Management).
 * - **Documentazione** is one screen opened on the app's section.
 *
 * An entry may carry its own `labelKey` where the rail names it differently from its manifest —
 * the same register is «Parametri» under an app and «Parametri di pianificazione» in the chapter.
 *
 * REPLENISHMENT IS ONE LEVEL — owner ruling 2026-10-24 (ADR 9023's amendment of that date). Two
 * additions let an app be drawn flat without touching a manifest: `view` lists a manifest CHILD as
 * an entry of its own, with the view's label (Proposte, Perché, Stock, Potenziale, Esploratore),
 * and `rule` draws the common entries' rule above an entry that is not common, where the app's
 * second group opens. A common entry may then stand in the first group without its rule
 * («Parametri»). Pricing, Compacting and Management keep their sub-entries. An entry whose page has
 * parts names them in `tabs` (Simulation's history, Proposals' «Tutte le celle dell'esecuzione»):
 * the page draws them as its tabs, the rail draws nothing for them and marks the entry on any of them.
 */

import type { NavEntry } from '../../features/manifest';
import type { ChapterNav } from '../../features/registry';
import {
  manifestViews, PARAMETERS_PATH, RAIL_APPS, parameterFamilyQuery, type RailAppDefinition, type RailAppEntry, type RailAppId,
} from '../../features/rail-apps';

// The list itself is in `src/features/rail-apps.ts` (ADR 9035) so the Documentation screen can read
// it; the rail's own modules and tests keep importing it from here.
export {
  ALLOCATION_CELLS_PATH, ALLOCATION_NOT_SENT_PATH, PARAMETER_APPROVALS_PATH, PARAMETERS_APP_PARAM, PARAMETERS_KEYS_PARAM, PARAMETERS_PATH, RAIL_APP_IDS, RAIL_APPS,
  SIMULATE_HISTORY_PATH, SIMULATE_PATH, SIMULATE_STAGE_PARAM, appReach, type RailAppDefinition, type RailAppEntry, type RailAppId,
} from '../../features/rail-apps';

/** The group every other screen lands in. Not an app: it has no entries of its own. */
export const FUTURE_GROUP = 'future';
export type RailGroupId = RailAppId | typeof FUTURE_GROUP;

/** One drawn link: the manifest's entry, and the query the rail opens it with. */
export interface RailLink {
  /** The manifest's entry — with the rail's own `labelKey` when the app entry gives one. */
  readonly entry: NavEntry;
  readonly query: Readonly<Record<string, string>>;
  /** Unique within the rail — Simulate and Documentation appear in more than one app. */
  readonly key: string;
  /**
   * The entry's other parts, which its page offers as tabs (`RailAppEntry.tabs`): not drawn, but on
   * one of them the entry is the planner's place. Empty for most entries.
   */
  readonly tabs: readonly string[];
  /**
   * The sub-entries drawn indented under this one, in manifest `order`, disabled ones left
   * out; empty for a plain entry. Each is a link of its own, opened with its parent's query.
   */
  readonly children: readonly RailLink[];
  /**
   * Drawn under a rule: the first of the app's common entries (brief part 2 §1), or the entry that
   * opens an app's second group (`rule`, Replenishment since 2026-10-24).
   */
  readonly common?: true;
}

export interface RailAppGroup {
  readonly id: RailAppId;
  readonly labelKey: string;
  readonly links: readonly RailLink[];
}

export interface RailLayout {
  /** The apps, in `RAIL_APPS` order; an app none of whose paths resolves is left out. */
  readonly apps: readonly RailAppGroup[];
  /**
   * Everything else, in manifest order: a chapter no app draws from as one row, and each
   * remaining screen of a chapter that has some drawn as a row of its own.
   */
  readonly future: readonly FutureItem[];
  /** Every query key some entry sets — what the rail clears when it opens a plain entry. */
  readonly queryKeys: ReadonlySet<string>;
}

export type FutureItem =
  | { readonly kind: 'chapter'; readonly chapter: ChapterNav }
  | { readonly kind: 'screen'; readonly chapter: ChapterNav; readonly entry: NavEntry };

function linkKey(to: string, query: Readonly<Record<string, string>>): string {
  const search = new URLSearchParams(query).toString();
  return search === '' ? to : [to, search].join('?');
}

/** The manifest children a `subEntries: 'manifest'` entry draws: in order, the usable ones.
 *  A sub-entry's key is its parent's, then its own — the Run view shares its parent's path. */
function subEntryLinks(
  entry: NavEntry, query: Readonly<Record<string, string>>, parentKey: string, withheld: (to: string) => boolean,
): RailLink[] {
  return [...(entry.children ?? [])]
    .sort((a, b) => a.order - b.order)
    .filter((child) => child.disabled !== true && !withheld(child.to))
    .map((child) => ({
      entry: child, query, key: [parentKey, linkKey(child.to, query)].join(' > '), children: [], tabs: [],
    }));
}

/**
 * The «Parametri» sub-entry an entry asks for with `parameters` (`docs/adr/9064`): the register's
 * own navigation entry, named as every app's «Parametri», opened on its app and key family. Drawn
 * last, and not at all where the register is not offered or is withheld.
 */
function parametersLink(
  app: RailAppEntry, register: NavEntry | undefined, parentKey: string, withheld: (to: string) => boolean,
): RailLink[] {
  if (app.parameters === undefined || register === undefined || register.disabled === true || withheld(register.to)) return [];
  const query = parameterFamilyQuery(app.parameters);
  const entry: NavEntry = { labelKey: 'nav.common.parameters', to: register.to, order: Number.MAX_SAFE_INTEGER };
  return [{ entry, query, key: [parentKey, linkKey(register.to, query)].join(' > '), children: [], tabs: [] }];
}

/**
 * The rail over the manifests. `withheld` names a path the person may not open — its route
 * declares a capability the platform denies them (ADR 9037, `rail-capabilities.ts`). Such an
 * entry is not drawn anywhere: not in its app, not as a sub-entry, and not under Future
 * expansions, where an inert row would still say "this exists and is not for you". As User
 * Management is not offered to someone its route would refuse (ADR 9014).
 */
export function railLayout(
  chapters: readonly ChapterNav[],
  apps: readonly RailAppDefinition[] = RAIL_APPS,
  withheld: (to: string) => boolean = () => false,
): RailLayout {
  const byPath = new Map<string, NavEntry>();
  for (const chapter of chapters) for (const entry of chapter.entries) byPath.set(entry.to, entry);
  const views = manifestViews(chapters);

  const groups: RailAppGroup[] = [];
  const queryKeys = new Set<string>();
  for (const app of apps) {
    const links: RailLink[] = [];
    for (const appEntry of app.entries) {
      const { to, query = {}, subEntries, labelKey, common, rule, view, tabs = [] } = appEntry;
      const manifestEntry = view === true ? views.get(to) : byPath.get(to);
      if (manifestEntry === undefined || manifestEntry.disabled === true || withheld(to)) continue;
      const key = linkKey(to, query);
      const children = subEntries === 'manifest'
        ? [...subEntryLinks(manifestEntry, query, key, withheld), ...parametersLink(appEntry, byPath.get(PARAMETERS_PATH), key, withheld)]
        : [];
      // A sub-entry's own query (a family's «Parametri») is the rail's too: cleared on the way out.
      for (const each of [query, ...children.map((child) => child.query)]) for (const name of Object.keys(each)) queryKeys.add(name);
      const entry = labelKey === undefined ? manifestEntry : { ...manifestEntry, labelKey };
      links.push({ entry, query, key, children, tabs, ...(common === true || rule === true ? { common: true as const } : {}) });
    }
    if (links.length > 0) groups.push({ id: app.id, labelKey: app.labelKey, links });
  }

  const shown = new Set(groups.flatMap((g) => g.links.map((l) => l.entry.to)));
  const future = chapters.flatMap((chapter): FutureItem[] => {
    if (!chapter.entries.some((e) => shown.has(e.to))) return [{ kind: 'chapter', chapter }];
    return chapter.entries
      .filter((entry) => !shown.has(entry.to) && !withheld(entry.to))
      .map((entry) => ({ kind: 'screen', chapter, entry }));
  });
  return { apps: groups, future, queryKeys };
}

/** Whether `currentPath` is the entry's screen or a drill-down beneath it. */
function under(currentPath: string, to: string): boolean {
  return currentPath === to || currentPath.startsWith(`${to}/`);
}

export interface ActiveRailLink {
  readonly app: RailAppId;
  /** The top-level entry — the one a closed group heading, or a collapsed-rail icon, stands for. */
  readonly link: RailLink;
  /** The sub-entry of `link` the planner is on, when `link` has sub-entries and one matches. */
  readonly child?: RailLink;
}

/**
 * The link the planner is on, and its app — longest path prefix, so a drill-down marks its
 * screen. An entry that carries a query (Simulate's `stage`, Documentation's `section`) is the
 * planner's place only when the address bar carries that query: with it absent, NO entry for
 * that path is marked and no group is opened for it.
 *
 * Not "the first listed", and not a guessed default: with no `?stage=` the Simulate screen has
 * no stage — it shows its required stage picker and runs nothing (`SimulateScreen.tsx`, the
 * `scope.stage === null` branch). Its `stage ?? 'distribute'` applies only inside an opened
 * `?simulation=`, where the saved simulation's own stage decides, which the chrome cannot read.
 * Marking either app there would claim a stage the screen does not show. Documentation with no
 * `?section=` shows every app's section, so it too belongs to none of them.
 *
 * Sub-entries compete under the same rule and carry their parent's query. A sub-entry wins a
 * tie with its own parent (the markdown's Run view IS `/inseason/markdown`), so on a screen
 * with views it is a view that is marked, never the heading alone.
 */
export function activeRailLink(
  layout: RailLayout,
  currentPath: string,
  params: Readonly<Record<string, string>> = {},
): ActiveRailLink | undefined {
  let best: { app: RailAppId; link: RailLink; child?: RailLink; length: number; specificity: number } | undefined;
  const matches = (query: Readonly<Record<string, string>>): boolean => Object.entries(query).every(([k, v]) => params[k] === v);
  // Same path, more of the link's query matched: a family's «Parametri» (`?app=&keys=`) beats the
  // app's general one (`?app=`) on the register, whichever the rail lists first (ADR 9064).
  const beats = (length: number, specificity: number): boolean =>
    best === undefined || length > best.length || (length === best.length && specificity > best.specificity);
  for (const app of layout.apps) {
    for (const link of app.links) {
      if (!matches(link.query)) continue;
      // The entry's own path, or one of the parts its page offers as tabs (Proposals' every cell).
      const own = [link.entry.to, ...link.tabs].filter((path) => under(currentPath, path));
      if (own.length > 0) {
        const length = Math.max(...own.map((path) => path.length));
        const specificity = Object.keys(link.query).length;
        if (beats(length, specificity)) best = { app: app.id, link, length, specificity };
      }
      for (const child of link.children) {
        if (!under(currentPath, child.entry.to) || !matches(child.query)) continue;
        const length = child.entry.to.length;
        const specificity = Object.keys(child.query).length;
        const tiesItsParent = best?.link === link && best.child === undefined && length === best.length;
        if (beats(length, specificity) || tiesItsParent) best = { app: app.id, link, child, length, specificity };
      }
    }
  }
  if (best === undefined) return undefined;
  return best.child === undefined
    ? { app: best.app, link: best.link }
    : { app: best.app, link: best.link, child: best.child };
}

/**
 * The query to navigate with, or `undefined` to let the shell carry the current one.
 *
 * The shell's `navigate(to)` carries the address bar's query to the next screen (G2.2, drill
 * preserves context). The rail keeps that, with one exception: the keys the rail itself sets
 * (`stage`, `section`) belong to the entry that set them, so they are cleared on the way to
 * any other entry and replaced on the way to one that sets its own. Without it the "section"
 * chip would follow the planner round the product after one visit to Documentation.
 *
 * **A run does not follow the planner through the menu** (owner ruling 2026-10-22, ADR 9039
 * amendment): a page opened from the rail starts from its own newest run. Every parameter whose
 * name ends in `RunId` ({@link RUN_BOUND_PARAM}) is cleared on the way — `runId`, and the
 * Cockpit's `exitRunId` / `exitLaneRunId`, the lifecycle pages' `transitionRunId`,
 * `withdrawalRunId`, `outletRunId`, `outletTransferRunId`. Drills, «vai al dettaglio» and links
 * built by a screen do not pass through here and keep naming their run.
 */
export function railNavigationParams(
  layout: RailLayout,
  link: RailLink,
  current: Readonly<Record<string, string>>,
): Record<string, string> | undefined {
  const dropped = (key: string): boolean => layout.queryKeys.has(key) || RUN_BOUND_PARAM.test(key);
  const stale = Object.keys(current).some(dropped);
  if (!stale && Object.keys(link.query).length === 0) return undefined;
  const carried = Object.fromEntries(Object.entries(current).filter(([key]) => !dropped(key)));
  return { ...carried, ...link.query };
}

/**
 * The parameters that name ONE run and mean nothing without it: matched on the key's shape
 * (`…RunId`), so the chrome names no chapter's parameter. Filters over a run (`runIsBroken`,
 * `repairsSizeRun`) and saved-simulation ids are not run ids and are carried as before.
 */
export const RUN_BOUND_PARAM = /runid$/i;

/**
 * The query a menu move (the rail, the collapsed rail, the palette, the Cockpit and My work
 * entries) goes out with: the current one without the run it names; `undefined` when it names no
 * run, so the shell carries the query as it always did.
 */
export function withoutRunParams(current: Readonly<Record<string, string>>): Record<string, string> | undefined {
  if (!Object.keys(current).some((key) => RUN_BOUND_PARAM.test(key))) return undefined;
  return Object.fromEntries(Object.entries(current).filter(([key]) => !RUN_BOUND_PARAM.test(key)));
}

/** Where a link goes: the screen itself, or for a screen with views its first usable view. */
export function firstUsable(entry: NavEntry): NavEntry {
  const views = [...(entry.children ?? [])].sort((a, b) => a.order - b.order);
  return views.find((v) => v.disabled !== true) ?? entry;
}

/** Whether a future item is where the planner is (arrived by deep link: the rail offers no way in). */
export function isFutureHere(item: FutureItem, currentPath: string, activeSlug: string | undefined): boolean {
  return item.kind === 'chapter' ? item.chapter.slug === activeSlug : under(currentPath, item.entry.to);
}
